import type { ColumnsType, ColumnType } from 'antd/es/table';

import Tooltip from '@/components/Tooltip/TooltipV2';
import Typo from '@/components/Typography/Typo';
import {
  CHART_ALL_FEEDBACK_ALIAS,
  CHART_FIELD,
  CHART_ROW_TOTAL_COLUMN_KEY,
  CHART_TOTAL_ROW_KEY,
} from '@/entities/chart/model/constants';
import { getChartMetricLabel } from '@/features/(charts)/chart-metric-policy';
import {
  ChartFieldMap,
} from '@/features/(charts)/chart-renderer/model/chart-maps';
import type { CrossTableColumn } from '@/features/(charts)/chart-renderer/ui/Cross/types';

import { ColumnBuilder } from './ColumnBuilder';

/**
 * 다중 시리즈 컬럼 빌더
 * 시리즈별 그룹 헤더와 컬럼을 생성
 */
export class MultiSeriesColumnBuilder extends ColumnBuilder {
  /**
   * 다중 시리즈 컬럼 생성
   * - baseColumns + 각 시리즈별 컬럼 그룹
   * - 각 시리즈의 showRowTotal에 따라 Row Total 컬럼 추가
   */
  buildColumns(): ColumnsType<CrossTableColumn> {
    const { chart, baseColumns, helpers, configs } = this.params;

    const columns: ColumnsType<any> = [...baseColumns];

    // 각 시리즈별로 컬럼 그룹 생성
    chart.series.forEach((series, seriesIndex) => {
      const seriesConfig = configs[seriesIndex];
      let seriesColumns: ColumnType<any>[] = [];
      if (!seriesConfig || !series.cross_field) return;

      if (series.cross_field.type === CHART_FIELD.FEEDBACK_ALL) {
        seriesColumns.push({
          title: 'All feedback',
          dataIndex: `series-${seriesIndex}-${CHART_ALL_FEEDBACK_ALIAS}`,
          width: 100,
          key: `series-${seriesIndex}-${CHART_ALL_FEEDBACK_ALIAS}`,
          render: (
            value: number,
            row: Record<string, StringNumberValue | null>,
            index: number,
          ) =>
            this.createCellRenderer(
              value,
              index,
              CHART_ALL_FEEDBACK_ALIAS,
              false,
              row.key === CHART_TOTAL_ROW_KEY,
              seriesIndex,
            ),
        });
      } else {
        const seriesColumnMapper = (series.cross_field.candidates || []).map(
          (alias) => ({
            title: (
              <Tooltip
                tooltipNode={helpers.getLabel(series.cross_field!.type, alias)}
              >
                <Typo>{helpers.getLabel(series.cross_field!.type, alias)}</Typo>
              </Tooltip>
            ),
            dataIndex: `series-${seriesIndex}-${alias}`,
            width: 100,
            key: `series-${seriesIndex}-${alias}`,
            render: (
              value: number,
              row: Record<string, StringNumberValue | null>,
              index: number,
            ) =>
              this.createCellRenderer(
                value,
                index,
                alias,
                false,
                row.key === CHART_TOTAL_ROW_KEY,
                seriesIndex,
              ),
          }),
        );
        seriesColumns.push(...seriesColumnMapper);
      }

      // 시리즈별 Row Total Column 추가 (show_row_total이 true인 경우)
      if (seriesConfig.showRowTotal) {
        seriesColumns.push({
          title: (
            <Tooltip tooltipNode="Row Total">
              <Typo>Row total</Typo>
            </Tooltip>
          ),
          dataIndex: `series-${seriesIndex}-${CHART_ROW_TOTAL_COLUMN_KEY}`,
          width: 100,
          key: `series-${seriesIndex}-${CHART_ROW_TOTAL_COLUMN_KEY}`,
          render: (
            value: number,
            row: Record<string, StringNumberValue | null>,
            index: number,
          ) =>
            this.createCellRenderer(
              value,
              index,
              CHART_ROW_TOTAL_COLUMN_KEY,
              false,
              true,
              seriesIndex,
            ),
        });
      }

      // 시리즈 그룹 헤더 추가 (measure 기반 이름 사용)
      const seriesTitle = ChartFieldMap.get(series.cross_field.type);

      if (series.cross_field.type === CHART_FIELD.FEEDBACK_ALL) {
        columns.push({
          title: `Feedabck (${getChartMetricLabel(series.measure.metric)})`,
          children: seriesColumns,
        });
      } else {
        columns.push({
          title: `${seriesTitle} (${getChartMetricLabel(
            series.measure.metric,
          )})`,
          children: seriesColumns,
        });
      }
    });

    return columns;
  }
}
